import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, Gauge, Hourglass, Inbox, Sparkles, TriangleAlert } from "lucide-mui";
import { api, type PlatformQueues, type PlatformSignalWorkflows } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import {
  compactNumber,
  drainLabel,
  evaluationsNeeded,
  perSecond,
} from "../../components/dashboard/platform/queueFormat.ts";
import SignalsPanel from "../../components/dashboard/platform/SignalsPanel.tsx";
import SignalWorkflowsPanel from "../../components/dashboard/platform/SignalWorkflowsPanel.tsx";
import EmptyState from "../../components/EmptyState.tsx";
import KpiTile from "../../components/KpiTile.tsx";
import { formatNumber, percent } from "../../utils/format.ts";

/**
 * The signal engine: the inbox per data type and every tenant's signal
 * workflows (backlog, output, history). The inference rigs, shared with the
 * classifier and agents, are on the overview.
 */
export default function PlatformSignalsPage() {
  const stats = useQuery({
    queryKey: keys.masterCompany.stats(),
    queryFn: api.getMasterCompanyStats,
    meta: { live: true },
  });
  const health = useQuery({
    queryKey: keys.platform.health(),
    queryFn: api.getPlatformHealth,
    meta: { live: true },
  });
  // Pushed with every five-minute sample; the rigs' own load also polls.
  const queues = useQuery({
    queryKey: keys.platform.queues(),
    queryFn: api.getPlatformQueues,
    refetchInterval: 60_000,
    meta: { live: true },
  });
  const workflows = useQuery({
    queryKey: keys.platform.signalWorkflows(),
    queryFn: api.getPlatformSignalWorkflows,
    meta: { live: true },
  });

  if (stats.isLoading || queues.isLoading) return <SignalsSkeleton />;
  if (!stats.data || !queues.data) {
    return (
      <EmptyState
        variant="panel"
        icon={<Inbox />}
        title="Signal stats are unavailable"
        description="The signal queues couldn't be loaded. Try again in a minute."
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      <KpiRow queues={queues.data} workflows={workflows.data ?? null} />
      <SignalsPanel signaling={stats.data.signaling} stats={health.data?.stats ?? null} queues={queues.data} />
      {workflows.data ? (
        <SignalWorkflowsPanel data={workflows.data} />
      ) : (
        workflows.isLoading && <Skeleton variant="rounded" height={360} />
      )}
    </Stack>
  );
}

function KpiRow({ queues, workflows }: { queues: PlatformQueues; workflows: PlatformSignalWorkflows | null }) {
  const inbox = queues.signal_inbox;
  const waiting = inbox.reduce((n, q) => n + q.pending, 0);
  const out = inbox.reduce((n, q) => n + (q.processed_1h ?? 0), 0);
  const incoming = inbox.reduce((n, q) => n + (q.added_1h ?? 0), 0);
  const net = out - incoming;
  const drain = drainLabel(waiting, net > 0 ? waiting / net : null);
  const clears = waiting === 0 || drain.growing ? drain.text : `Clears in ${drain.text}`;

  // One record decided for one workflow: what the rigs deliver, whatever the fan-out.
  const evaluations1h = inbox.reduce((n, q) => n + q.evaluations_1h, 0);
  const evaluations24h = inbox.reduce((n, q) => n + q.evaluations_24h, 0);
  const needed1h = inbox.reduce((n, q) => n + evaluationsNeeded(q), 0);
  const behind = evaluations1h < needed1h;

  const all = workflows?.tenants?.flatMap((t) => t.workflows) ?? [];
  const add = (pick: (w: (typeof all)[number]) => number | null) => all.reduce((n, w) => n + (pick(w) ?? 0), 0);
  const evaluated24h = add((w) => w.evaluated_24h);
  const matched24h = add((w) => w.matched_24h);
  const failed24h = add((w) => w.failed_24h);
  const qualified = add((w) => w.qualified);
  const tokens24h = add((w) => w.tokens_24h);
  const active = all.filter((w) => w.active).length;

  return (
    <Grid container spacing={2.5}>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <KpiTile
          label="Waiting for the model"
          icon={<Hourglass />}
          color={drain.growing ? "warning" : "primary"}
          emphasis={drain.growing}
          value={waiting}
          caption={`${clears} · ${formatNumber(out)}/h out · ${formatNumber(incoming)}/h in`}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <KpiTile
          label="Evaluations / s"
          icon={<Gauge />}
          color={behind ? "warning" : "primary"}
          emphasis={behind}
          value={perSecond(evaluations1h)}
          caption={`Needs ${perSecond(needed1h)}/s · ${formatNumber(evaluations24h)} in 24h · ${compactNumber(tokens24h)} tokens · ${formatNumber(active)} active workflow${active === 1 ? "" : "s"}`}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <KpiTile
          label="Matched · 24h"
          icon={<Sparkles />}
          color="success"
          value={matched24h}
          caption={`${percent(matched24h, evaluated24h)} of evaluated · ${formatNumber(qualified)} qualified in ${workflows?.days ?? 14} days`}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
        <KpiTile
          label="Failed calls · 24h"
          icon={failed24h ? <TriangleAlert /> : <CircleCheck />}
          color={failed24h ? "error" : "success"}
          emphasis={failed24h > 0}
          value={failed24h}
          caption={`${formatNumber(queues.engine.serving_instances)} of ${formatNumber(queues.inference.length)} rigs serving`}
        />
      </Grid>
    </Grid>
  );
}

function SignalsSkeleton() {
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2.5}>
        {[0, 1, 2, 3].map((i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Skeleton variant="rounded" height={148} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" height={360} />
      <Skeleton variant="rounded" height={360} />
    </Stack>
  );
}
